import React, { useEffect, useRef } from 'react';
import { Link } from 'gatsby';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { Icon } from '@components/icons';
import { usePrefersReducedMotion } from '@hooks';
import { useLanguage } from '../../context/language';

const StyledProjectsSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;

  .services-grid {
    ${({ theme }) => theme.mixins.resetList};
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 16px;
    width: 100%;
    margin-top: 20px;
  }

  .seo-links {
    width: 100%;
    margin-top: 26px;

    h3 {
      margin: 0 0 10px;
      font-size: var(--fz-xl);
    }

    ul {
      ${({ theme }) => theme.mixins.fancyList};
      margin: 0;
    }
  }
`;

const StyledService = styled.li`
  height: 100%;

  .service-inner {
    ${({ theme }) => theme.mixins.boxShadow};
    ${({ theme }) => theme.mixins.flexBetween};
    flex-direction: column;
    align-items: flex-start;
    position: relative;
    height: 100%;
    padding: 1.75rem 1.5rem;
    border-radius: var(--border-radius);
    background: var(--surface);
    border: 1px solid var(--surface-border);
    transition: var(--transition);

    &:hover,
    &:focus-within {
      transform: translateY(-6px);
    }
  }

  .service-top {
    ${({ theme }) => theme.mixins.flexBetween};
    width: 100%;
    margin-bottom: 20px;

    .folder {
      color: var(--green);
      svg {
        width: 34px;
        height: 34px;
      }
    }

    .service-link {
      color: var(--light-slate);
      display: inline-flex;
      align-items: center;
      gap: 5px;
      font-size: var(--fz-xs);
      font-family: var(--font-mono);

      svg {
        width: 16px;
        height: 16px;
      }
    }
  }

  .service-title {
    margin: 0 0 10px;
    color: var(--lightest-slate);
    font-size: var(--fz-xxl);
  }

  .service-description {
    color: var(--light-slate);
    font-size: var(--fz-md);

    p {
      margin: 0;
    }
  }

  .service-tech-list {
    display: flex;
    flex-wrap: wrap;
    padding: 0;
    margin: 18px 0 0;
    list-style: none;

    li {
      font-family: var(--font-mono);
      font-size: var(--fz-xxs);
      line-height: 1.8;
      color: var(--lightest-slate);
      margin-right: 13px;
    }
  }
`;

const Projects = () => {
  const { content, language } = useLanguage();
  const services = content.services.items;
  const seoPages =
    language === 'fr'
      ? [
        {
          label: 'Developpeur mobile senior a Paris',
          url: '/fr/senior-mobile-developer',
        },
        {
          label: 'Consultant Flutter Kotlin iOS',
          url: '/fr/flutter-kotlin-ios-consultant',
        },
      ]
      : [
        {
          label: 'Senior mobile developer in Paris',
          url: '/en/senior-mobile-developer',
        },
        {
          label: 'Flutter Kotlin iOS consultant',
          url: '/en/flutter-kotlin-ios-consultant',
        },
      ];

  const revealTitle = useRef(null);
  const revealServices = useRef([]);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealTitle.current, srConfig());
    revealServices.current.forEach((ref, i) => sr.reveal(ref, srConfig(i * 100)));
  }, [prefersReducedMotion, content]);

  return (
    <StyledProjectsSection id="services">
      <h2 className="numbered-heading" ref={revealTitle}>
        {content.services.heading}
      </h2>

      <ul className="services-grid">
        {services.map((item, i) => {
          const { external, title, description, tech } = item;

          return (
            <StyledService key={i} ref={el => (revealServices.current[i] = el)}>
              <div className="service-inner">
                <header>
                  <div className="service-top">
                    <div className="folder">
                      <Icon name="Folder" />
                    </div>

                    {external && (
                      <a href={external} className="service-link" target="_blank" rel="noreferrer">
                        {content.services.detailsLabel}
                        <Icon name="External" />
                      </a>
                    )}
                  </div>

                  <h3 className="service-title">{title}</h3>

                  <div className="service-description">
                    <p>{description}</p>
                  </div>
                </header>

                <footer>
                  {tech && (
                    <ul className="service-tech-list">
                      {tech.map((techItem, index) => (
                        <li key={index}>{techItem}</li>
                      ))}
                    </ul>
                  )}
                </footer>
              </div>
            </StyledService>
          );
        })}
      </ul>

      <div className="seo-links">
        <h3>{language === 'fr' ? 'Pages detaillees' : 'Detailed Pages'}</h3>
        <ul>
          {seoPages.map((item, i) => (
            <li key={i}>
              <Link to={item.url}>{item.label}</Link>
            </li>
          ))}
        </ul>
      </div>
    </StyledProjectsSection>
  );
};

export default Projects;
